import * as React from "react";
import {
    Link,
    makeStyles,
    Text
} from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";
import { CardSection } from "../../components/CardSection";
import { CodeBlock } from "../../components/CodeBlock";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "2rem clamp(1rem, 5vw, 4rem)",
        width: "80%",
        margin: "0 auto",
        maxWidth: "1400px",
    },
    ul: {
        paddingLeft: "1.25rem",
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
    },
    ol: {
        paddingLeft: "1.25rem",
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
    }
});

export const Id = "a276529b-73ad-4572-8f2a-0a2aa0c58c18";
export const Title = "How to use Ianus Guard inside PCFs?";
export const Subtitle = "Integrate license validation into your PowerApps Component Framework controls";
export const Description = "Step-by-step guide for securing your PCFs with Ianus Guard licensing";
export const Keywords = ["developer", "react", "pcf", "dataset", "field"];

export const Component: React.FC = React.memo(() => {
    const styles = useStyles();
    const navigate = useNavigate();

    const codeSamplePlaceholder = `<IanusProvider
    publisherId='[PUBLISHEREXTERNALID]'
    productId='[PRODUCTEXTERNALID]'
    publicKeys={[\`[PUBLICKEY]\`]}
    environmentType="dataverse"
    environmentIdentifier={(this.props.pcfContext as unknown as { orgSettings: { attributes: { organizationid: string }}}).orgSettings.attributes.organizationid ?? this.props.pcfContext.webAPI}
    dataProvider={this.props.pcfContext.webAPI}
>
    <Label>{this.props.name}</Label>
</IanusProvider>`;

    const codeSampleReplaced = `<IanusProvider
    publisherId='41fd96d4-26eb-44fc-a8a3-4ce890fd955c'
    productId='4219c0e1-1b29-4b94-abb3-f7d9ac5617c7'
    publicKeys={[
        \`-----BEGIN PUBLIC KEY-----
        MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAznOMDcbaUIRERL772Hip
        3/znf6MEkd7R2bEhyFzRbTZ0+KxBo20uZLlPv/zQRbaLg8VVMdbaY8FX4+hNBmnw
        hnyX6V1CwqEXp9DEHHOYuddcv20DhmcB6yyXh8KMUBVmUd7kYwE3goYyMzjMiNYr
        QcLoFUkx4IT6PqT1EcaKbBrQTQjhxgq3wyt/ZKW73aM2VuA9oLnyWW+5K031Evsa
        gRQxeyL43JwDJ8SGmEl+fuoh0htiI4sZNmdeyYk5zM7BdKWYA61j7a66I219ajbP
        E/+hAWsY4/jco60G+M0+x9Dd5gl75utQ0Qna9a+UxNHEHpqbiqpKCVtBhxvWoKVa
        1QIDAQAB
        -----END PUBLIC KEY-----\`
    ]}
    environmentType="dataverse"
    environmentIdentifier={(this.props.pcfContext as unknown as { orgSettings: { attributes: { organizationid: string }}}).orgSettings.attributes.organizationid ?? this.props.pcfContext.webAPI}
    dataProvider={this.props.pcfContext.webAPI}
>
    <Label>{this.props.name}</Label>
</IanusProvider>`;

    const resourceXml = `<feature-usage>
    <uses-feature name="WebAPI" required="true" />
    <uses-feature name="WebAPI.retrieveMultipleRecords" required="true" />
</feature-usage>`;

    return (
        <div className={styles.container}>
            <CardSection title="Prerequisites">
                <Text>
                    Ianus licensing can be seamlessly integrated into PowerApps Component Framework (PCF) controls. You can
                    either use an existing PCF project or follow a Microsoft guide to create a new one.
                    Be sure to have imported our Ianus Guard solution, preferably via <Link href="https://marketplace.microsoft.com/en-us/product/saas/ianuasoftware.ianus_guard?tab=overview">Microsoft AppSource</Link> or alternatively from <Link href="https://github.com/Ianua-Software/Ianus-Clients/blob/main/solutions/IanusGuard_managed.zip" target="_blank">
                        GitHub
                    </Link> into your organization before starting.
                </Text>
            </CardSection>

            <CardSection title="Recommended setup guides">
                <ul className={styles.ul}>
                    <li>
                        <Link href="https://learn.microsoft.com/en-us/power-apps/developer/component-framework/react-controls-platform-libraries" target="_blank">
                            Microsoft guide for React-based PCFs
                        </Link>
                    </li>
                    <li>
                        <Link href="https://learn.microsoft.com/en-us/power-apps/developer/component-framework/implementing-controls-using-typescript?tabs=before" target="_blank">
                            General PCF control creation guide
                        </Link>
                    </li>
                </ul>
            </CardSection>

            <CardSection title="Install Ianus NPM package">
                <CodeBlock code="npm install --save @ianua/ianus-dataverse-react-fluentui8" />
            </CardSection>

            <CardSection title="Wrap your root component in <IanusProvider>">
                <Text>
                    This typically happens in your root entry component:
                </Text>
                <br />
                <br />
                <CodeBlock code={codeSamplePlaceholder} />
                <br />
                <br />
                <Text>
                    In above example, the <code>Label</code> child is the protected component, which would be your actual app in real use.
                </Text>
            </CardSection>

            <CardSection title="Replace placeholders with your real values">
                <Text>
                    As a rule of thumb, all information that you need to enter has a "Copy Button" next to it.
                    <br/>
                    Where to find the information for replacing:
                </Text>
                <ul className={styles.ul}>
                    <li><b>PUBLISHEREXTERNALID</b>: ExternalId found on your publisher form</li>
                    <li><b>PRODUCTEXTERNALID</b>: ExternalId found on your product form</li>
                    <li><b>PUBLICKEY</b>: Found on your product form as "Public Key" inside "Product Versions"</li>
                </ul>
                <Text>
                    We advice you to start with dual signing early on, meaning that you should embed the public key from Ianus Guard portal, as well as a public key that you generate yourself.
                    That way you can always leave the platform if you ever feel the need to do so, without disturbing your operations (but please reach out first, maybe we can help).
                    A detailled instruction on how to do so can be found on <Link href="https://github.com/Ianua-Software/Ianus-Clients/tree/main/standalone" target="_blank">GitHub</Link>.
                </Text>
                <Text>
                    Don’t have an Ianus Guard account yet? Follow the{" "}
                    <Link onClick={() => navigate("/docs/article/5f27bdcf-1055-4c66-8a39-eee72ec0a397")}>
                        Publisher getting started guide
                    </Link>.
                </Text>
            </CardSection>

            <CardSection title="Example with real values">
                <CodeBlock code={codeSampleReplaced} />
            </CardSection>

            <CardSection title="Update feature-usage in manifest">
                <Text>
                    For being able to retrieve and update licenses using Web API, you need to make sure that the feature-usage section in the <code>ControlManifest.Input.xml</code> contains at least these two entries:
                </Text>
                <br />
                <br />
                <CodeBlock code={resourceXml} />
                <br />
                <br />
                <Text>
                    That way your PCF is allowed to fetch, create and update licenses via Web API.
                </Text>
            </CardSection>

            <CardSection title="Final steps">
                <Text>
                    Ianus Guard will validate the license automatically. If no license is found, an error dialog shows up, giving the possibility to enter a license - your component won’t render.
                </Text>
            </CardSection>
        </div>
    );
});
